import React, { useEffect, useState, useContext } from "react";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";

const MilestoneTemplateList = ({ onEdit }) => {
  const [templates, setTemplates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedTemplate, setExpandedTemplate] = useState(null);

  const { tenant_id, loadingUser } = useContext(AuthContext);

  // Load customers for filter
  useEffect(() => {
    if (loadingUser || !tenant_id) return;

    secureAxios
      .get("/api/customers", { params: { tenant_id } })
      .then((res) => setCustomers(res.data))
      .catch((err) => console.error("Error loading customers", err));
  }, [tenant_id, loadingUser]);

  // Load templates
  useEffect(() => {
    if (loadingUser || !tenant_id) return;

    setLoading(true);
    const params = { tenant_id, include_items: "true" };
    if (selectedCustomer) {
      params.customer_id = selectedCustomer;
    }

    secureAxios
      .get("/api/milestone-templates", { params })
      .then((res) => {
        setTemplates(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading milestone templates:", err);
        setLoading(false);
      });
  }, [tenant_id, selectedCustomer, loadingUser]);

  // Filter templates by search term
  const filteredTemplates = templates.filter(
    (template) =>
      template.template_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = async (templateId, templateName) => {
    if (!window.confirm(`Are you sure you want to delete "${templateName}"? This will also delete all milestone items in this template.`))
      return;

    try {
      await secureAxios.delete(`/api/milestone-templates/${templateId}`, {
        params: { tenant_id },
      });
      setTemplates(templates.filter((t) => t.template_id !== templateId));
      alert("Template deleted successfully");
    } catch (err) {
      console.error("Error deleting template:", err);
      if (err.response?.status === 409) {
        alert("Cannot delete template - it may be in use by authorizations");
      } else {
        alert("Error deleting template");
      }
    }
  };

  const toggleExpand = (templateId) => {
    setExpandedTemplate(expandedTemplate === templateId ? null : templateId);
  };

  if (loadingUser || loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading milestone templates...</p>
      </div>
    );
  }

  return (
    <div>
      <h2>All Milestone Templates</h2>

      {/* Filters Row */}
      <div className="row mb-3">
        <div className="col-md-6">
          <label className="form-label">Filter by Customer:</label>
          <select
            className="form-select"
            value={selectedCustomer}
            onChange={(e) => setSelectedCustomer(e.target.value)}
          >
            <option value="">All Customers</option>
            {customers.map((c) => (
              <option key={c.customer_id} value={c.customer_id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-6">
          <label className="form-label">Search:</label>
          <input
            type="text"
            className="form-control"
            placeholder="Search by template name, customer, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {filteredTemplates.length === 0 ? (
        <div className="alert alert-secondary">
          <p className="mb-0">No milestone templates found. Create your first template to get started.</p>
        </div>
      ) : (
        <>
          {/* Desktop/Tablet View */}
          <div className="d-none d-md-block">
            {filteredTemplates.map((template) => (
              <div key={template.template_id} className="card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0">
                      {template.template_name}
                      {template.is_default && (
                        <span className="badge bg-primary ms-2">Default</span>
                      )}
                    </h5>
                    <small className="text-muted">
                      Customer: {template.customer_name || "All Customers"}
                    </small>
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => toggleExpand(template.template_id)}
                    >
                      {expandedTemplate === template.template_id ? "Collapse" : "View Items"}
                    </button>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => onEdit(template)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(template.template_id, template.template_name)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                
                {template.description && (
                  <div className="card-body py-2">
                    <p className="mb-0 text-muted">{template.description}</p>
                  </div>
                )}

                {/* Expanded Items View */}
                {expandedTemplate === template.template_id && (
                  <div className="card-body border-top">
                    <h6>Milestone Items ({template.items?.length || 0})</h6>
                    {template.items && template.items.length > 0 ? (
                      <table className="table table-sm table-bordered mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: "60px" }}>Order</th>
                            <th>Milestone Name</th>
                            <th>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {template.items
                            .sort((a, b) => a.sequence_order - b.sequence_order)
                            .map((item) => (
                              <tr key={item.item_id}>
                                <td className="text-center">{item.sequence_order}</td>
                                <td>{item.milestone_name}</td>
                                <td className="text-muted">{item.description || "-"}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-muted mb-0">No milestone items defined yet.</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Mobile View */}
          <div className="d-block d-md-none">
            {filteredTemplates.map((template) => (
              <div key={template.template_id} className="card mb-3">
                <div className="card-body">
                  <h5 className="card-title">
                    {template.template_name}
                    {template.is_default && (
                      <span className="badge bg-primary ms-2">Default</span>
                    )}
                  </h5>
                  <p className="card-text">
                    <strong>Customer:</strong> {template.customer_name || "All Customers"}
                    <br />
                    {template.description && (
                      <>
                        <strong>Description:</strong> {template.description}
                        <br />
                      </>
                    )}
                    <strong>Items:</strong> {template.items?.length || 0} milestones
                  </p>
                  
                  <div className="d-flex flex-wrap gap-2">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => toggleExpand(template.template_id)}
                    >
                      {expandedTemplate === template.template_id ? "Hide Items" : "View Items"}
                    </button>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => onEdit(template)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(template.template_id, template.template_name)}
                    >
                      Delete
                    </button>
                  </div>

                  {/* Expanded Items for Mobile */}
                  {expandedTemplate === template.template_id && template.items && (
                    <div className="mt-3 pt-3 border-top">
                      <h6>Milestone Items:</h6>
                      <ul className="list-group list-group-flush">
                        {template.items
                          .sort((a, b) => a.sequence_order - b.sequence_order)
                          .map((item) => (
                            <li key={item.item_id} className="list-group-item px-0">
                              <strong>{item.sequence_order}.</strong> {item.milestone_name}
                              {item.description && (
                                <small className="d-block text-muted">{item.description}</small>
                              )}
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default MilestoneTemplateList;